// pages/SectionPage.jsx
import React from 'react';
import { FaLayerGroup } from 'react-icons/fa'; // Using a more relevant icon for "Section"
import GenericCrudPage from '../components/GenericCrudPage';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// 1. Validation Logic for the Section form
const validateSectionForm = (form) => {
  const errors = {};
  if (!form.name || !form.name.trim()) {
    errors.name = "Section name is required";
  } else if (form.name.length > 50) {
    errors.name = "Section name must be less than 50 characters";
  }
  return errors;
};

// 2. Configuration object for Section CRUD operations
const sectionConfig = {
  baseUrl: BASE_URL,
  endpoints: {
    fetch: '/master/getSection',
    // Assuming a consistent naming convention for save endpoint
    save: '/master/saveSection', 
    update: '/master/updateSection',
    delete: '/master/deleteSection'
  },
  validateForm: validateSectionForm,
  initialFormState: {
    name: "",
    isActive: 1, // Default to active
    user: "" // The generic component will handle the user ID
  },
  entityName: 'Section',
  // The API seems to return data within a 'section' property
  dataKey: 'section' 
};

// 3. Form fields configuration
const formFields = [
  { 
    name: "name", 
    label: "Section Name", 
    icon: FaLayerGroup, 
    required: true, 
    placeholder: "e.g., Morning Session, A-Section" 
  },
];

// 4. Table columns configuration
const tableColumns = [
  {
    key: 'exName',
    label: 'Section Name',
    sortable: true,
  }
  // The "Status" and "Actions" columns are handled by GenericCrudPage by default
];

// 5. CSV export headers configuration
const csvHeaders = [
  { key: 'exName', label: 'Name' },
  { 
    key: 'exIsActive', 
    label: 'Status',
    // Optional formatter for better CSV readability
    formatter: (value) => (value ? 'Active' : 'Inactive') 
  }
];

// 6. Function to transform form data into the API payload format
const transformPayload = (form, editId, userId) => ({
  exName: form.name.trim(),
  exIsActive: form.isActive,
  user: userId, // Pass the userId from the generic component
  ...(editId && { id: editId }) // Add id only when editing
});

// 7. Function to transform API data into the form's state for editing
const transformToForm = (section) => ({
  name: section.exName || "",
  isActive: section.exIsActive,
  user: section.user || ""
});

// 8. Function to generate fields for the "View Details" modal
const getViewFields = (section) => {
  if (!section) return [];
  return [
    { label: "Section Name", value: section.exName, icon: FaLayerGroup },
    // The status is implicitly shown in the table, but can be added here if needed
    { 
      label: "Status", 
      value: section.exIsActive ? "Active" : "Inactive", 
      isBadge: true, // Custom property to render as a badge in the view modal
      variant: section.exIsActive ? 'success' : 'secondary'
    }
  ];
};

/**
 * Renders the Sections page using the reusable GenericCrudPage component.
 * It provides all the necessary configurations for handling CRUD operations for sections.
 */
export default function SectionPage() {
  return (
    <GenericCrudPage
      title="Section"
      pluralTitle="Sections"
      config={sectionConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      emptyIcon={FaLayerGroup}
      searchPlaceholder="Search by section name..."
      csvHeaders={csvHeaders}
      csvFilename="sections_export.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
    />
  );
}
